import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'
import { first } from 'rxjs/operators'
import { ApiService } from './api.service'
import { ImageResult, ResizeOptions } from './directives'
import { NotifyService } from './notifications/notify.service'
import { getFirebaseApp } from './firebase-init'
import { getStorage, ref, uploadBytes, uploadString, uploadBytesResumable, getDownloadURL, listAll } from 'firebase/storage'
import { GlobalService } from './global.service'
import { NgxSpinnerService } from 'ngx-spinner'

function s2ab(s: string): ArrayBuffer {
  const buf: ArrayBuffer = new ArrayBuffer(s.length)
  const view: Uint8Array = new Uint8Array(buf)
  // tslint:disable-next-line: no-bitwise
  for (let row = 0; row !== s.length; ++row) view[row] = s.charCodeAt(row) & 0xFF
  return buf
}

let env: any

export interface IResize {
  width: number,
  height: number
}

@Injectable({ providedIn: 'root' })
export class UploadService {

  private storage: any = null // Firebase Storage instance
  private extensions = {
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/svg+xml': '.svg+xml',
  }
  private resizeOptions: ResizeOptions
  private resizeAreaId: string

  constructor(
    @Inject(DOCUMENT) private document: any,
    private notify: NotifyService,
    private g: GlobalService,
    private spin: NgxSpinnerService,
    private api: ApiService
  ) {
    env = (window as any).y4wenvs.ENV
    // test for SSR
    if (this.g.isBrowser) {
      const app = getFirebaseApp(env.firebaseConfig)
      this.storage = getStorage(app)
    }
  }

  getUrl(path: string): Promise<any> {
    const pathRef = ref(this.storage, path)
    return getDownloadURL(pathRef).catch((error) => {
      switch (error.code) {
        case 'storage/object-not-found':
          console.count(error.code)
          break
        case 'storage/unauthorized':
          console.count(error.code)
          break
        case 'storage/canceled':
          console.count(error.code)
          break
        case 'storage/unknown':
          console.count(error.code)
          break
      }
    })
  }


  listAllFiles(path: string): Promise<any> {
    return listAll(ref(this.storage, path))
  }

  uploadFiles(files: any[], path, spinner = true, notifications = true): Promise<any[]> {
    if (spinner) this.spin.show()
    return new Promise(async resolve => {
      const filesUrls = []

      for (const file of files) {
        const fileName = `${+ new Date()}_${file.name}`
        const filePath = `${path}/${fileName}`
        const fileRef = ref(this.storage, filePath)
        let uploadedFile = null

        try {
          uploadedFile = await uploadBytes(fileRef, file, { cacheControl: 'private, max-age=15552000' })
        } catch (e) {
          console.error(e)
          filesUrls.push(null)
        }
        if (uploadedFile) {
          try {
            const downloadURL = await this.getUrl(filePath)
            filesUrls.push({ url: `${downloadURL}`, name: fileName, count: files.length, type: file.type.split('/')[1] })
          } catch (e) {
            console.error(e)
            filesUrls.push(null)
          }
        }
      }
      if (notifications) this.notify.update('files uploaded', 'btn-success', 3000)
      if (spinner) this.spin.hide()
      resolve(filesUrls)
    })
  }

  async uploadXls$$(wbout, nick, seller, wkb) {
    const fileRef = ref(this.storage, `${nick}/${seller}/${wkb}`)
    const metadata = { contentType: 'application/vnd.ms-excel' }
    const uploadTask = uploadBytesResumable(fileRef, new Blob([s2ab(wbout)]), metadata)
    return new Promise(function (resolve, reject) {
      uploadTask.on('state_changed', (snapshot) => {
      }, (err) => {
        reject()
      }, async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
        resolve(downloadURL)
      }
      )
    })
  }

  async uploadBase64(base64: string, name: string, path: string) {
    const ext = base64.replace(/(data:)([\w\/]+)(;base64)(.+)/gm, `$2`)
    const filePath = `${path}/${name}` // .${this.extensions[ext]}`
    const fileRef = ref(this.storage, filePath)
    let uploadedFile: any = null
    try {
      uploadedFile = await uploadString(fileRef, base64, 'data_url', { cacheControl: 'private, max-age=15552000' })
    } catch (e) {
      console.error(e)
      return null
    }
    if (uploadedFile) {
      try {
        const downloadURL = await this.getUrl(filePath)
        return downloadURL
      } catch (e) {
        console.error(e)
        return null
      }
    }
  }

  resizeImage(base64Image: string, resizeOptions: IResize | IResize[] = null): Promise<string | string[]> {
    if (!this.resizeOptions && !resizeOptions) return Promise.resolve(base64Image)
    return this.api.post('dataImport/resizeImage', {
      base64: base64Image,
      resize: Array.isArray(resizeOptions) ? resizeOptions : [resizeOptions],
      filename: `file-${Date.now()}`
    }, true).pipe(first()).toPromise()
      .then((res: any) => {
        if (!res?.success) {
          // On failure, fall back to the original image(s), matching the
          // backend per-entry fallback when sharp cannot process an image.
          return Array.isArray(resizeOptions) ? resizeOptions.map(() => base64Image) : base64Image
        }
        return Array.isArray(resizeOptions) ? res.result : res.result[0]
      })
  }
}
